/**
 * frontend/src/pages/valuation/_useSignOffRecord.ts
 *
 * VALUATION-B3 sign-off — what the screen reads (engagement, frozen record,
 * documents, preflight) and the three writes it makes (status, finalize,
 * produce). The comments below speak from ValuationSignOff.tsx, the one caller.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 */
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  fetchValuationDocuments,
  getValuationEngagement,
  updateValuationEngagement,
  type ValuationDocumentRow,
} from '@/api/valuation'
import {
  fetchActiveConfig,
  fetchSnapshot,
  fetchSnapshotHistory,
  finalizeValuation,
  preflightValuation,
  produceValuationDocument,
} from '@/api/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import { finalizedToastText } from './_FinalizeCard'
import type { ValuationEngagement } from '@/types/valuation'
import type {
  ConfigResponse,
  PreflightResponse,
  SnapshotHistoryRow,
  SnapshotResponse,
} from '@/types/valuationB3'
import { statusLabel, type ValuationStatus } from './_shared'

export function useSignOffRecord(
  valuationId: string | undefined,
  hasSavedDraft: boolean,
  updatedAt: string | null,
  rationale: string,
  setRationale: (value: string) => void,
  canConclude: boolean,
) {
  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [config, setConfig] = useState<ConfigResponse | null>(null)
  const [snapshot, setSnapshot] = useState<SnapshotResponse | null>(null)
  const [history, setHistory] = useState<SnapshotHistoryRow[]>([])
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [finalizing, setFinalizing] = useState(false)
  // POL-B item 1: the server's refusal to sign without a set-aside reason, shown inline.
  const [finalizeRefusal, setFinalizeRefusal] = useState<string | null>(null)
  const [producing, setProducing] = useState(false)
  const [documents, setDocuments] = useState<ValuationDocumentRow[]>([])
  const [pdfStatus, setPdfStatus] = useState<string | null>(null)

  /** Snapshot + history together — after a finalize both have moved. */
  const loadRecord = useCallback(async (id: string) => {
    const [snap, hist] = await Promise.all([fetchSnapshot(id), fetchSnapshotHistory(id)])
    setSnapshot(snap)
    setHistory(hist.snapshots)
  }, [])

  const loadDocuments = useCallback(async (id: string) => {
    const res = await fetchValuationDocuments(id)
    setDocuments(res.documents)
  }, [])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => { if (live) setEngagement(en) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    loadRecord(valuationId).catch((err: unknown) => {
      // A missing record is not an error — nothing has been signed yet. Only a
      // genuine failure to read it is, and the screen still works without it.
      if (live) toast.error(formatApiError(err))
    })
    loadDocuments(valuationId).catch((err: unknown) => {
      // An engagement with no documents is not an error — nothing produced yet.
      if (live) toast.error(formatApiError(err))
    })
    fetchActiveConfig()
      .then(c => { if (live) setConfig(c) })
      .catch(() => { /* the review panel falls back to the preflight numbers */ })
    return () => { live = false }
  }, [valuationId, loadRecord, loadDocuments])

  /**
   * Re-run the gate when the tuning inputs change — NOT on the rationale. The
   * rationale changes no number the engine returns (it is volatile in the
   * fingerprint, `core/valuation/config_codec.py`), so typing it must not
   * trigger a re-run: `canFinalize` below enforces the blank-rationale rule
   * client-side, and `/finalize` enforces it server-side, strictly, at the
   * point of freezing.
   */
  useEffect(() => {
    if (!valuationId || !hasSavedDraft) return
    let live = true
    const timer = setTimeout(() => {
      preflightValuation(valuationId)
        .then(p => { if (live) { setPreflight(p); setPreflightError(null) } })
        .catch((err: unknown) => { if (live) setPreflightError(formatApiError(err)) })
    }, 400)
    return () => { live = false; clearTimeout(timer) }
  }, [valuationId, hasSavedDraft, updatedAt])

  async function setStatus(next: ValuationStatus): Promise<boolean> {
    if (!valuationId) return false
    setSaving(true)
    try {
      const updated = await updateValuationEngagement(valuationId, { status: next })
      setEngagement(updated)
      toast.success(`Status set to “${statusLabel(next)}”.`)
      return true
    } catch (err: unknown) {
      toast.error(formatApiError(err))
      return false
    } finally {
      setSaving(false)
    }
  }

  /** `reason` replaces the typed one: the owner's record carries a fixed reason (VAL-PATH-P6b). */
  async function doFinalize(reason?: string) {
    if (!valuationId || !hasSavedDraft) return
    setFinalizing(true)
    setFinalizeRefusal(null)
    try {
      await finalizeValuation(valuationId, (reason ?? rationale).trim())
      // The server cleared the draft's signoff_reason with this finalize; the box starts empty for the next version.
      setRationale('')
      // WALKFIX-C AMEND_1: no version numbers in the toast (owner ruling 2026-10-25).
      toast.success(finalizedToastText(canConclude))
      await loadRecord(valuationId)
      // VAL-FINALIZE-STATE-2: the preflight in state was computed against the OLD version; drop it
      // and read it again so the bar does not offer Re-finalize as v{N+2} on a version just signed.
      setPreflight(null)
      preflightValuation(valuationId)
        .then(p => { setPreflight(p); setPreflightError(null) })
        .catch((err: unknown) => setPreflightError(formatApiError(err)))
      // The band on the row was just restated by the server; re-read it rather
      // than patching the copy in state from the response.
      const fresh = await getValuationEngagement(valuationId)
      setEngagement(fresh)
      // VAL-SIGNOFF-GATE-1: only a credentialed finalize signs — the status
      // marker follows what actually happened, not what this screen used to
      // assume happened. An owner's finalize records the calculation; it does
      // not become "Signed off" just because a snapshot now exists (see the
      // module docstring and _SignOffStatus.tsx: status is a workflow marker,
      // the snapshot's signed_role is the attestation).
      const target: ValuationStatus = canConclude ? 'signed_off' : 'calculated'
      const settled = fresh.status !== target ? await setStatus(target) : true
      // VAL-WALK-SCREENS-1 W7: tell the shell once the status is really settled,
      // so its badge and side menu refresh without a navigation.
      if (settled) {
        window.dispatchEvent(new CustomEvent('valuation:engagement-changed', { detail: { id: valuationId } }))
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { error?: { code?: string; message?: string } } } }).response?.data?.error
      // WALKFIX-A F7 — the approved sentence is shown inline on its own; the
      // raw "409 - Conflict | CODE" toast is for every other refusal only.
      if (body?.code === 'SET_ASIDE_REASON_REQUIRED' && body.message) setFinalizeRefusal(body.message)
      else toast.error(formatApiError(err))
    } finally {
      setFinalizing(false)
    }
  }

  /**
   * Print the frozen record. The version on screen is sent explicitly rather
   * than letting the server pick "current" — if a colleague re-finalized while
   * this tab sat open, the mismatch comes back as the gate's refusal instead of
   * silently handing over a document with a different basis than the one
   * reviewed here.
   *
   * This no longer downloads anything itself — it archives the document(s)
   * and lists what got produced. Files are saved from the card's list.
   */
  async function handleProduce() {
    if (!valuationId) return
    setProducing(true)
    try {
      const response = await produceValuationDocument(
        valuationId,
        snapshot?.snapshot?.version,
      )
      setPdfStatus(response.pdf_status)
      const count = response.documents.length
      toast.success(`Documents produced — ${count} file${count === 1 ? '' : 's'} ready.`)
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      // Success or 409, the server's list and on-file flag are the truth.
      await Promise.all([loadDocuments(valuationId), loadRecord(valuationId)]).catch(
        (e: unknown) => toast.error(formatApiError(e)),
      )
      setProducing(false)
      // The left rail reads the same on-file flag for its NEXT marker.
      window.dispatchEvent(new CustomEvent('valuation:engagement-changed', { detail: { id: valuationId } }))
    }
  }

  return {
    engagement, loadError, saving, config, snapshot, history, preflight, preflightError,
    finalizing, finalizeRefusal, producing, documents, pdfStatus, setStatus, doFinalize, handleProduce,
  }
}
